import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { inferRouterOutputs } from "@trpc/server";
import { format, formatDistanceToNow } from "date-fns";
import { useDeferredValue, useMemo, useState } from "react";
import { toast } from "sonner";
import { PageContainer } from "@/components/page-container";
import { PageHeader } from "@/components/page-header";
import { AdminGuard } from "@/components/role-guard";
import { TableSkeleton } from "@/components/skeletons";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Heading } from "@/components/ui/typography";
import type { SystemRole } from "@/lib/system-roles";
import { trpc } from "@/utils/trpc";
import type { AppRouter } from "../../../server/dist/routers";

type RoleFilter = "all" | SystemRole;
type AdminUser =
  inferRouterOutputs<AppRouter>["admin"]["listUsers"]["users"][number];

const ROLE_FILTER_OPTIONS: ReadonlyArray<{
  value: RoleFilter;
  label: string;
}> = [
  { value: "all", label: "All roles" },
  { value: "worker", label: "Workers" },
  { value: "site_manager", label: "Site managers" },
  { value: "admin", label: "Admins" },
];

const ROLE_META: Record<SystemRole, { label: string; tone: string }> = {
  worker: { label: "Worker", tone: "bg-muted text-foreground" },
  site_manager: {
    label: "Site Manager",
    tone: "bg-emerald-100 text-emerald-800",
  },
  admin: { label: "Admin", tone: "bg-rose-100 text-rose-800" },
};

const ROLE_ACTION_LABEL: Record<SystemRole, string> = {
  worker: "Set as worker",
  site_manager: "Set as site manager",
  admin: "Set as admin",
};

const ROLE_ACTION_OPTIONS: ReadonlyArray<SystemRole> = [
  "worker",
  "site_manager",
  "admin",
];

export default function AdminUsers() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<RoleFilter>("all");
  const deferredSearch = useDeferredValue(search.trim());

  const listUsersOptions = trpc.admin.listUsers.queryOptions({
    search: deferredSearch || undefined,
    role: roleFilter,
  });

  const usersQuery = useQuery({
    ...listUsersOptions,
    placeholderData: (previousData) => previousData,
  });

  const updateRoleMutation = useMutation({
    ...trpc.admin.updateUserRole.mutationOptions(),
    onMutate: ({ userId }) => {
      queryClient.cancelQueries({ queryKey: listUsersOptions.queryKey });
      return { userId };
    },
    onError: (error) => {
      toast.error(error.message || "Failed to update user role");
    },
    onSuccess: (_data, variables) => {
      toast.success(`Role updated to ${ROLE_META[variables.systemRole].label}`);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: listUsersOptions.queryKey });
    },
  });

  const sendPasswordResetMutation = useMutation({
    ...trpc.admin.sendPasswordReset.mutationOptions(),
    onError: (error) => {
      toast.error(error.message || "Failed to send password reset");
    },
    onSuccess: () => {
      toast.success("Password reset email sent");
    },
  });

  const users = useMemo<AdminUser[]>(
    () => usersQuery.data?.users ?? [],
    [usersQuery.data?.users]
  );

  const isLoading = usersQuery.isLoading || usersQuery.isFetching;

  return (
    <AdminGuard>
      <PageContainer className="flex flex-col gap-6">
        <PageHeader
          title="User management"
          description="Search, filter and adjust roles for every account."
        />

        <section className="space-y-4">
          <DirectoryFilters
            roleFilter={roleFilter}
            searchValue={search}
            onSearchChange={setSearch}
            onRoleFilterChange={setRoleFilter}
          />

          <DirectoryTable
            users={users}
            isLoading={isLoading}
            isMutating={
              updateRoleMutation.isPending ||
              sendPasswordResetMutation.isPending
            }
            error={usersQuery.error}
            onRoleChange={(userId, currentRole, nextRole) => {
              if (nextRole === currentRole) {
                toast.info("User already has this role");
                return;
              }

              updateRoleMutation.mutate({
                userId,
                systemRole: nextRole,
              });
            }}
            onSendPasswordReset={(userId) => {
              sendPasswordResetMutation.mutate({ userId });
            }}
          />
        </section>
      </PageContainer>
    </AdminGuard>
  );
}

function DirectoryFilters({
  roleFilter,
  searchValue,
  onRoleFilterChange,
  onSearchChange,
}: {
  roleFilter: RoleFilter;
  searchValue: string;
  onRoleFilterChange: (value: RoleFilter) => void;
  onSearchChange: (value: string) => void;
}) {
  return (
    <header className="surface-panel flex flex-col gap-4 p-4">
      <div className="flex flex-col gap-3">
        <div>
          <Heading variant="subsection" as="h2">
            Directory
          </Heading>
          <p className="text-sm text-muted-foreground">
            Review account metadata and enforce the correct access level.
          </p>
        </div>

        <Select
          value={roleFilter}
          onValueChange={(value) => onRoleFilterChange(value as RoleFilter)}
        >
          <SelectTrigger
            className="h-9 w-[11.5rem]"
            aria-label="Filter by system role"
          >
            <SelectValue placeholder="All roles" />
          </SelectTrigger>
          <SelectContent>
            {ROLE_FILTER_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Input
        placeholder="Search by name, email or company"
        aria-label="Search users"
        value={searchValue}
        onChange={(event) => onSearchChange(event.target.value)}
        className="max-w-sm"
      />
    </header>
  );
}

function DirectoryTable({
  users,
  isLoading,
  isMutating,
  error,
  onRoleChange,
  onSendPasswordReset,
}: {
  users: AdminUser[];
  isLoading: boolean;
  isMutating: boolean;
  error: unknown;
  onRoleChange: (
    userId: string,
    currentRole: SystemRole,
    nextRole: SystemRole
  ) => void;
  onSendPasswordReset: (userId: string) => void;
}) {
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [passwordResetTarget, setPasswordResetTarget] =
    useState<AdminUser | null>(null);

  if (error) {
    const message = errorAsMessage(error);
    return (
      <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
        Failed to load users. {message ?? "Please try again."}
      </div>
    );
  }

  if (isLoading) {
    return <TableSkeleton columns={5} />;
  }

  if (users.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-muted p-6 text-center text-sm text-muted-foreground">
        No users found for the current filters.
      </div>
    );
  }

  return (
    <>
      <div className="space-y-3">
        <div className="space-y-3 md:hidden">
          {users.map((userRecord) => (
            <DirectoryCard
              key={userRecord.id}
              user={userRecord}
              isMutating={isMutating}
              onOpenDetails={() => setSelectedUser(userRecord)}
              onRoleChange={onRoleChange}
              onSendPasswordReset={() => setPasswordResetTarget(userRecord)}
            />
          ))}
        </div>

        <div className="table-panel hidden md:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="px-4">Name</TableHead>
                <TableHead className="px-4">Email</TableHead>
                <TableHead className="px-4">Role</TableHead>
                <TableHead className="px-4">Company</TableHead>
                <TableHead className="px-4 text-right">Last active</TableHead>
                <TableHead className="px-4 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((userRecord) => (
                <DirectoryRow
                  key={userRecord.id}
                  user={userRecord}
                  isMutating={isMutating}
                  onOpenDetails={() => setSelectedUser(userRecord)}
                  onRoleChange={onRoleChange}
                  onSendPasswordReset={() => setPasswordResetTarget(userRecord)}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <UserDetailsDialog
        user={selectedUser}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedUser(null);
          }
        }}
      />

      <PasswordResetConfirmDialog
        user={passwordResetTarget}
        onOpenChange={(open) => {
          if (!open) {
            setPasswordResetTarget(null);
          }
        }}
        onConfirm={() => {
          if (passwordResetTarget) {
            onSendPasswordReset(passwordResetTarget.id);
            setPasswordResetTarget(null);
          }
        }}
      />
    </>
  );
}

function DirectoryRow({
  user,
  isMutating,
  onOpenDetails,
  onRoleChange,
  onSendPasswordReset,
}: {
  user: AdminUser;
  isMutating: boolean;
  onOpenDetails: () => void;
  onRoleChange: (
    userId: string,
    currentRole: SystemRole,
    nextRole: SystemRole
  ) => void;
  onSendPasswordReset: () => void;
}) {
  const role = normaliseSystemRole(user.role);
  const meta = ROLE_META[role];
  const company = user.company?.trim() || "Not set";

  return (
    <TableRow>
      <TableCell className="px-4 py-3 text-sm font-medium text-foreground">
        <Button
          variant="link"
          className="h-auto max-w-[12rem] justify-start truncate p-0 text-left font-medium text-foreground"
          onClick={onOpenDetails}
        >
          {user.name}
        </Button>
      </TableCell>
      <TableCell className="px-4 py-3 text-sm text-muted-foreground">
        <span className="truncate block max-w-[16rem]">{user.email}</span>
      </TableCell>
      <TableCell className="px-4 py-3">
        <Badge className={`${meta.tone} font-semibold`}>{meta.label}</Badge>
      </TableCell>
      <TableCell className="px-4 py-3 text-sm text-muted-foreground">
        <span className="truncate block max-w-[14rem]">{company}</span>
      </TableCell>
      <TableCell className="px-4 py-3 text-xs text-muted-foreground text-right">
        {formatLastActive(user.lastActive)}
      </TableCell>
      <TableCell className="px-4 py-3 text-right">
        <ActionsMenu
          currentRole={role}
          disabled={isMutating}
          disablePasswordReset={user.anonymised}
          onSelect={(nextRole) => onRoleChange(user.id, role, nextRole)}
          onSendPasswordReset={onSendPasswordReset}
        />
      </TableCell>
    </TableRow>
  );
}

function DirectoryCard({
  user,
  isMutating,
  onOpenDetails,
  onRoleChange,
  onSendPasswordReset,
}: {
  user: AdminUser;
  isMutating: boolean;
  onOpenDetails: () => void;
  onRoleChange: (
    userId: string,
    currentRole: SystemRole,
    nextRole: SystemRole
  ) => void;
  onSendPasswordReset: () => void;
}) {
  const role = normaliseSystemRole(user.role);
  const meta = ROLE_META[role];
  const company = user.company?.trim() || "Not set";

  return (
    <div className="surface-panel space-y-3 p-4">
      <div className="space-y-1">
        <Button
          variant="link"
          className="h-auto p-0 text-sm font-semibold text-foreground"
          onClick={onOpenDetails}
        >
          {user.name}
        </Button>
        <p className="text-xs text-muted-foreground break-all">{user.email}</p>
      </div>

      <div className="flex items-center justify-between gap-3">
        <Badge className={`${meta.tone} font-semibold`}>{meta.label}</Badge>
        <span className="text-xs text-muted-foreground">
          {formatLastActive(user.lastActive)}
        </span>
      </div>

      <p className="text-sm text-muted-foreground">
        <span className="font-medium text-foreground">Company: </span>
        {company}
      </p>

      <ActionsMenu
        currentRole={role}
        disabled={isMutating}
        disablePasswordReset={user.anonymised}
        onSelect={(nextRole) => onRoleChange(user.id, role, nextRole)}
        onSendPasswordReset={onSendPasswordReset}
        triggerClassName="w-full"
      />
    </div>
  );
}

function UserDetailsDialog({
  user,
  onOpenChange,
}: {
  user: AdminUser | null;
  onOpenChange: (open: boolean) => void;
}) {
  const role = user ? normaliseSystemRole(user.role) : null;
  const detailRows = user
    ? [
        { label: "Email", value: user.email, monospace: false },
        {
          label: "Role",
          value: ROLE_META[role ?? "worker"].label,
          monospace: false,
        },
        {
          label: "Company",
          value: user.company?.trim() || "Not set",
          monospace: false,
        },
        {
          label: "Job title",
          value: user.jobTitle?.trim() || "Not set",
          monospace: false,
        },
        {
          label: "Phone number",
          value: user.phoneNumber?.trim() || "Not set",
          monospace: false,
        },
        {
          label: "Created",
          value: formatExactDate(user.createdAt),
          monospace: false,
        },
        {
          label: "Last active",
          value: formatLastActive(user.lastActive, true),
          monospace: false,
        },
        {
          label: "Email verified",
          value: user.emailVerified ? "Yes" : "No",
          monospace: false,
        },
        {
          label: "Banned",
          value: user.banned ? "Yes" : "No",
          monospace: false,
        },
        {
          label: "Anonymised",
          value: user.anonymised ? "Yes" : "No",
          monospace: false,
        },
        { label: "User ID", value: user.id, monospace: true },
      ]
    : [];

  return (
    <Dialog open={Boolean(user)} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>{user ? user.name : "User details"}</DialogTitle>
          <DialogDescription>
            Review the existing account, profile, and access metadata for this
            user.
          </DialogDescription>
        </DialogHeader>

        {user ? (
          <div className="space-y-6">
            <div className="flex flex-wrap items-center gap-2">
              <Badge
                className={`${ROLE_META[role ?? "worker"].tone} font-semibold`}
              >
                {ROLE_META[role ?? "worker"].label}
              </Badge>
              <Badge variant={user.emailVerified ? "approved" : "outline"}>
                {user.emailVerified ? "Email verified" : "Email not verified"}
              </Badge>
              {user.banned ? <Badge variant="destructive">Banned</Badge> : null}
              {user.anonymised ? (
                <Badge variant="warning">Anonymised</Badge>
              ) : null}
            </div>

            <div className="overflow-x-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-56">Field</TableHead>
                    <TableHead>Value</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {detailRows.map((row) => (
                    <TableRow key={row.label}>
                      <TableCell className="font-medium text-foreground">
                        {row.label}
                      </TableCell>
                      <TableCell
                        className={`break-words text-muted-foreground ${row.monospace ? "font-meta text-xs" : "text-sm"}`}
                      >
                        {row.value}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function ActionsMenu({
  currentRole,
  disabled,
  disablePasswordReset,
  onSelect,
  onSendPasswordReset,
  triggerClassName,
}: {
  currentRole: SystemRole;
  disabled: boolean;
  disablePasswordReset: boolean;
  onSelect: (nextRole: SystemRole) => void;
  onSendPasswordReset: () => void;
  triggerClassName?: string;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled}
          className={triggerClassName}
        >
          Actions
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {ROLE_ACTION_OPTIONS.map((option) => (
          <DropdownMenuItem
            key={option}
            onSelect={(event) => {
              event.preventDefault();
              onSelect(option);
            }}
            disabled={disabled || option === currentRole}
          >
            {ROLE_ACTION_LABEL[option]}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={(event) => {
            event.preventDefault();
            onSendPasswordReset();
          }}
          disabled={disabled || disablePasswordReset}
        >
          Send password reset
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function PasswordResetConfirmDialog({
  user,
  onOpenChange,
  onConfirm,
}: {
  user: AdminUser | null;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog open={Boolean(user)} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Send password reset</DialogTitle>
          <DialogDescription>
            A password reset email will be sent to{" "}
            <span className="font-medium text-foreground">{user?.email}</span>.
            The link expires in 1 hour.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onConfirm}>Send reset email</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function errorAsMessage(error: unknown) {
  if (error instanceof Error) {
    return error.message;
  }

  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as { message?: unknown }).message === "string"
  ) {
    return (error as { message: string }).message;
  }

  return null;
}

function formatLastActive(value: string | null, exact = false) {
  if (!value) {
    return "No activity";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "No activity";
  }

  if (exact) {
    return `${format(date, "d MMM yyyy, HH:mm")} (${formatDistanceToNow(date, {
      addSuffix: true,
    })})`;
  }

  return formatDistanceToNow(date, {
    addSuffix: true,
  });
}

function formatExactDate(value: string | null) {
  if (!value) {
    return "—";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return format(date, "d MMM yyyy, HH:mm");
}

function normaliseSystemRole(value: unknown): SystemRole {
  if (value === "admin" || value === "site_manager" || value === "worker") {
    return value;
  }

  return "worker";
}
